import {
  AvatarCard, OutlinedIcons, useCurrentTime,
} from '@marageti/z4-lib';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import {
  GetParticipantsResponse, ParticipantInfo,
} from '@marageti/z4-sdk/lib/travel';
import {
  Box, Card, CardHeader, Divider, Stack, Typography,
} from '@mui/material';
import React from 'react';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { getInitials } from '../../../utils/helpers';

const getFullName = (participant: ParticipantInfo): string => [participant.givenName, participant.familyName].filter(Boolean).join(' ');

const getAgentParticipantTitle = (participant: ParticipantInfo): string => {
  if (participant.personType === PersonType.Agent) {
    switch (participant.userInfo?.[0]?.roles?.[0]) {
      case RoleName.AGENT_SUPPORT:
        return 'Support';

      case RoleName.AGENT:
      case RoleName.AGENT_ADMIN:
      default:
        return 'Travel Specialist';
    }
  }

  return '';
};

const {
  OutlinedClockIcon,
} = OutlinedIcons;

const Section = ({
  'data-e2e': e2e = undefined,
  title = undefined,
  children = undefined,
}: {
  ['data-e2e']?: string,
  title?: React.ReactNode,
  children?: React.ReactNode,
}) => (
  <Box sx={{ mb: 0 }} data-e2e={e2e}>
    {title && <Typography variant="h4" sx={{ mb: 2 }}>{title}</Typography>}
    {children}
  </Box>
);

const TeamItem = ({
  fullName,
  title,
  timeZone,
}: {
  fullName: string,
  title: string,
  timeZone: string,
}) => {
  const currentTime = useCurrentTime(timeZone);

  return (
    <CardHeader
      avatar={(
        <AvatarCard size={40} sx={{ marginRight: 1 }}>
          {getInitials(fullName)}
        </AvatarCard>
                )}
      title={fullName}
      subheader={(
        <Stack
          direction="row"
          sx={{ alignItems: 'center' }}
        >
          {title}
          {' '}
          {currentTime && (
            <OutlinedClockIcon
              sx={{
                fontSize: '0.8rem',
                ml: 1,
                mr: 0.5,
              }}
            />
          )}
          {currentTime}
        </Stack>
                )}
      titleTypographyProps={{
        variant: 'body1',
        fontWeight: 'bold',
      }}
    />
  );
};

interface TeamCardProps {
  assignedAgentId: string | undefined;
  participants: GetParticipantsResponse | undefined;
}

const TeamCard = ({
  assignedAgentId,
  participants,
}: TeamCardProps) => {
  const participantValues = participants ? Object.values(participants) : [];
  const assignedAgentParticipant = participantValues.find((participant) => participant.id === assignedAgentId);
  const agentParticipantEntries = participantValues.filter((participant) => (participant.id !== assignedAgentId && participant.personType === PersonType.Agent));
  const sortedAgentParticipantEntries = agentParticipantEntries
    .sort((aParticipant, bParticipant) => aParticipant.givenName.localeCompare(bParticipant.givenName));
  // Prepend the assigned agent
  if (assignedAgentParticipant) sortedAgentParticipantEntries.unshift(assignedAgentParticipant);
  if (participantValues.length === 0) return null;

  return (
    <Section>
      <Card>
        <Stack spacing={2}>
          {sortedAgentParticipantEntries.map((participant) => (
            <TeamItem
              key={participant.id}
              title={getAgentParticipantTitle(participant)}
              fullName={getFullName(participant)}
              timeZone={participant.timeZone?.name || ''}
            />
          ))}

        </Stack>
        <Box sx={{
          pt: 0, pr: 3, pb: 3, pl: 3,
        }}
        >
          <Divider sx={{ mb: 2 }} />
          <Typography variant="caption">
            Always communicate through this messaging platform to ensure you&apos;re covered by our terms of service, privacy policy and other safeguards.
          </Typography>
        </Box>
      </Card>
    </Section>
  );
};

export default TeamCard;
